import {
  ChangeEvent,
  FC,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAtom, useSetAtom } from 'jotai';
import type { Response } from 'lib/request/types';
import { SidePanel } from '@carbon/ibm-products';
import { SidePanelResizeHandle } from 'components/resizable-side-panel';
import { useEscapeToClose } from 'lib/hooks/useEscapeToClose';
import {
  ContentSwitcher,
  Dropdown,
  InlineNotification,
  Switch,
  TextArea,
  TextInput,
} from '@carbon/react';
import { createMultiFileAtom, type MultiFileEntry } from 'lib/atoms/file-atom';
import { FileDropField, WORKBOOK_ACCEPT } from './file-drop-field';
import {
  writeFailureMessage,
  type MutationArg,
  type WriteFailure,
} from 'lib/atoms/section-mutation';
import { ExternalTitleSupportFields } from './external-title-support-fields';
import {
  type RelatedItem,
  type RelatedItemKind,
  type RelatedItemTypeOption,
  isProvisionableKind,
  LINK_DOCUMENT_TYPE_OPTIONS,
  titleSupportDetailsFrom,
  type TitleSupportDetails,
} from './types';
import {
  ProvisionUploadSection,
  type ProvisionUploadHandle,
} from './provision-upload-section';
import {
  VestingDocumentSection,
  type VestingDocumentHandle,
} from 'components/vesting-documents/vesting-document-section';
import {
  RECORD_KIND_CONFIGS,
  recordConfig,
  type PickerRow,
} from './record-kind-configs';
import { RecordPickerTable } from './record-picker-table';
import { EXTERNAL_TITLE_SUPPORT_PICKER } from './external-title-support-picker';
import {
  addRelatedItemAtom,
  relatedItemWriteUrl,
  externalTitleSupportUploadUrl,
  externalTitleSupportsUrl,
  relatedItemsUrl,
  updateRelatedItemAtom,
  type RelatedItemsHost,
} from './atoms';
import styles from './link-document-panel.module.scss';

// One SidePanel for both add and edit. A kind Dropdown branches the body: record kinds
// use a multi-select picker; link/note/upload use their own inputs (uploads via DirectUpload).

const NOTE_TITLE_MAX = 50;

// The six kinds that link an existing record — read off the config map so the
// list isn't maintained twice.
const RECORD_PICKER_KINDS = Object.keys(
  RECORD_KIND_CONFIGS
) as RelatedItemKind[];

// Record-link kinds may carry an optional external URL + label on the row itself
// (e.g. "View on EnergyLink"). Uploads don't — a URL would compete with the file.
const EXTERNAL_LINK_KINDS: RelatedItemKind[] = RECORD_PICKER_KINDS;

// A stored url renders straight into the row's <a href>, so only http(s) is allowed —
// a javascript:/data: href would run on click. Mirrors the model's format validation.
export const isWebUrl = (value: string): boolean =>
  /^https?:\/\//i.test(value.trim());

// Endpoints return different shapes (a related_items row vs a workbook `show`
// payload); only `id` is read, so only `id` is typed.
type PostOutcome = { ok: boolean; message?: string };

const settledOk = (result: PromiseSettledResult<PostOutcome>): boolean =>
  result.status === 'fulfilled' && result.value.ok;

type Mutation = (arg: MutationArg<RelatedItem>) => unknown;

// `upload` answers bytes the corpus already holds with 422 plus the id of the row
// holding them — one workbook supports many tracts, so re-uploading one is the
// likeliest failure here. Reading that id turns a dead end into a link. CRM hosts
// put it at the top level; TSW hosts in the coded envelope's details.
type UploadFailure = WriteFailure & {
  externalTitleSupportId?: number;
  errors?: ({ details?: { externalTitleSupportId?: number } } | null)[];
};

// One wrapper for the create POST, the upload and the edit PATCH: resolves an
// outcome rather than rejecting, so callers can count partial failures.
const mutate = async (
  run: Mutation,
  url: string,
  method: 'POST' | 'PATCH',
  data: unknown
): Promise<PostOutcome & { duplicateId?: number }> => {
  const response = (await run({ url, method, data })) as Response<
    unknown,
    UploadFailure
  >;
  if (response.type === 'success') return { ok: true };
  if (response.type === 'error') {
    return {
      ok: false,
      message: writeFailureMessage(response.data),
      duplicateId:
        response.data?.externalTitleSupportId ??
        response.data?.errors?.[0]?.details?.externalTitleSupportId,
    };
  }
  return { ok: false, message: response.message };
};

interface Props {
  open: boolean;
  mode: 'add' | 'edit';
  /** The host record the related_items endpoints hang off. */
  host: RelatedItemsHost;
  /** Shell root the slide-in SidePanel anchors its margin to. */
  selectorPageContent?: string;
  document?: RelatedItem | null;
  onClose: () => void;
  /** Fully done — close the panel and refresh the list. */
  onSaved: () => void;
  /** Refresh the list without closing (partial-success on a multi-link). */
  onRefresh?: () => void;
  /** Names the record being linked to, when the host has several. */
  addSubtitle?: string;
}

export const LinkDocumentPanel: FC<Props> = ({
  open,
  mode,
  host,
  selectorPageContent = '#main-content',
  document,
  onClose,
  onSaved,
  onRefresh,
  addSubtitle = 'Add or link an item to this record.',
}) => {
  const isEdit = mode === 'edit';

  const filesAtom = useMemo(() => createMultiFileAtom(), []);
  const [files, dispatchFiles] = useAtom(filesAtom);
  const addDocument = useSetAtom(addRelatedItemAtom);
  const updateDocument = useSetAtom(updateRelatedItemAtom);

  const [kind, setKind] = useState<RelatedItemKind | null>(null);
  const [note, setNote] = useState('');
  const [fileLabel, setFileLabel] = useState('');
  const [fileNotes, setFileNotes] = useState('');
  const [supportDetails, setSupportDetails] = useState<TitleSupportDetails>({});
  const [extUrl, setExtUrl] = useState('');
  const [extLabel, setExtLabel] = useState('');
  const [selectedRecords, setSelectedRecords] = useState<
    Map<string, PickerRow>
  >(new Map());
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Record kinds offer a second mode: upload + provision a new document instead of
  // picking an existing one. The section owns its own state; the panel only tracks
  // whether it's active and whether it's ready to save.
  const [provisionMode, setProvisionMode] = useState(false);
  const [provisionReady, setProvisionReady] = useState(false);
  const [uploadNewWorkbook, setUploadNewWorkbook] = useState(false);
  const provisionRef = useRef<ProvisionUploadHandle>(null);
  // The section owns the search, the upload branch and both POSTs.
  const [vestingReady, setVestingReady] = useState(false);
  const vestingRef = useRef<VestingDocumentHandle>(null);

  const clearInputs = () => {
    setNote('');
    setFileLabel('');
    setFileNotes('');
    setSupportDetails({});
    setExtUrl('');
    setExtLabel('');
    setSelectedRecords(new Map());
    dispatchFiles({ type: 'reset' });
    setProvisionMode(false);
    setProvisionReady(false);
    setVestingReady(false);
    setUploadNewWorkbook(false);
    setError(null);
    setPending(false);
  };

  // Seed on open (prefill in edit); clear on close.
  useEffect(() => {
    if (!open) {
      clearInputs();
      setKind(null);
      return;
    }
    if (isEdit && document) {
      setKind(document.kind);
      setNote(document.body ?? '');
      setExtUrl(document.url ?? '');
      setExtLabel(document.label ?? '');
      setFileLabel(document.fileName ?? '');
      setFileNotes(document.body ?? '');
      setSupportDetails(titleSupportDetailsFrom(document));
      setSelectedRecords(new Map());
      dispatchFiles({ type: 'reset' });
      setError(null);
      setPending(false);
    } else if (!isEdit) {
      clearInputs();
      setKind(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, document]);

  const handleKindChange = (next: RelatedItemKind | null) => {
    setKind(next);
    clearInputs();
  };

  const activeConfig = recordConfig(kind);
  // A workbook differs from every other kind at nearly every step — its own picker,
  // its own fields, its own controller — so the difference is named once here rather
  // than spelled out at each branch.
  const isWorkbook = kind === 'external_title_support';
  const isVesting = kind === 'vesting_document';
  // Edit must not offer a search for a different document; same rule as linkingWorkbooks.
  const linkingVesting = isVesting && !isEdit;
  // Edit must show the judgement fields, not the picker.
  const linkingWorkbooks = isWorkbook && !isEdit && !uploadNewWorkbook;

  // Scoped to this tract so the picker never offers a workbook it already supports.
  const workbookPicker = useMemo(
    () => ({
      ...EXTERNAL_TITLE_SUPPORT_PICKER,
      extraParams: {
        tractId: host.tractId,
        titleSupportWorkspaceId: host.titleSupportWorkspaceId,
      },
    }),
    [host.tractId, host.titleSupportWorkspaceId]
  );

  const kindOptions = LINK_DOCUMENT_TYPE_OPTIONS.filter(
    (option) => !option.requires || host[option.requires]
  );

  const visibleFiles = files.filter((f) => !f.hidden);
  const uploading = visibleFiles.some((f) => f.status === 'uploading');
  const completedFiles = visibleFiles.filter(
    (f): f is MultiFileEntry & { file: string } =>
      f.status === 'complete' && !!f.file
  );

  // Same rule on add and edit, so a row can't be given a link it can never edit.
  // Hidden in provision mode — the optional link belongs to a picked record, not
  // to an upload that provisions its own document.
  const showExternalSection =
    !!kind && EXTERNAL_LINK_KINDS.includes(kind) && !provisionMode;

  // A present-but-non-web url blocks Save (empty is fine — presence is enforced per-kind).
  const externalUrlInvalid = extUrl.trim() !== '' && !isWebUrl(extUrl);

  const canSubmit = (() => {
    if (pending || uploading || !kind) return false;
    if (isEdit && !document) return false;
    // The external link field is hidden in provision mode, so a stale invalid url there
    // must not silently wedge Save — it isn't submitted on this branch anyway.
    if (!provisionMode && externalUrlInvalid) return false;

    // Upload-&-provision: the section reports when a file is uploaded + config chosen.
    if (provisionMode) return provisionReady;
    // Arms only while linking: nothing on a vesting row is editable here, and Save in edit
    // mode would PATCH related_items/:id — a different record sharing the integer id.
    if (isVesting) return linkingVesting && vestingReady;

    if (isEdit) {
      switch (kind) {
        case 'note':
          return note.trim() !== '';
        case 'external_link':
          return extUrl.trim() !== '';
        case 'uploaded_file':
          return fileLabel.trim() !== '';
        default:
          return true; // record-ish edits amend the optional external link
      }
    }

    if (RECORD_PICKER_KINDS.includes(kind)) return selectedRecords.size > 0;
    if (kind === 'external_link') return extUrl.trim() !== '';
    if (kind === 'note') return note.trim() !== '';
    if (kind === 'uploaded_file') return completedFiles.length > 0;
    if (isWorkbook)
      return linkingWorkbooks
        ? selectedRecords.size > 0
        : completedFiles.length > 0;
    return false;
  })();

  const toggleRecord = (row: PickerRow) => {
    const pickerConfig = linkingWorkbooks
      ? EXTERNAL_TITLE_SUPPORT_PICKER
      : activeConfig;
    if (!pickerConfig) return;
    const id = pickerConfig.getRowId(row);
    setSelectedRecords((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, row);
      return next;
    });
  };

  const optionalExternal = () => {
    const url = extUrl.trim();
    if (!url) return {};
    const label = extLabel.trim();
    return label ? { url, label } : { url };
  };

  const postOne = (data: unknown): Promise<PostOutcome> =>
    mutate(addDocument, relatedItemsUrl(host), 'POST', data);

  // NULL means nobody has said; false means somebody looked and said no.
  const curatedFields = () => ({
    // Re-selecting the placeholder writes '', which is neither "nobody has said"
    // (null) nor an answer — collapse it back to null at the write boundary.
    provenance: supportDetails.source?.trim() || null,
    covers_runsheet: supportDetails.fullRunsheet ?? null,
    covers_ownership: supportDetails.ownership ?? null,
    // null, not '': the create path drops nils so linking a workbook cannot clear
    // a comment someone already wrote on it. An explicit PATCH still clears.
    comments: fileNotes.trim() || null,
  });

  // Both endpoints take the curated fields alongside the write, so each path is a
  // single request. The form is filled in before either happens, and a follow-up
  // PATCH could leave the workbook stored with the judgement fields lost.
  const uploadWorkbook = async (
    file: (typeof completedFiles)[number]
  ): Promise<PostOutcome> => {
    const fields = curatedFields();
    const uploaded = await mutate(
      addDocument,
      externalTitleSupportUploadUrl(host),
      'POST',
      { attachment: file.file, external_title_support: fields }
    );
    if (uploaded.ok || uploaded.duplicateId === undefined) return uploaded;

    // Already in the corpus — link that row to this tract instead of failing. This
    // is also what makes a retry recoverable: the second POST is always a duplicate,
    // because the first one stored the bytes. Two requests and not one only because
    // nothing can know the bytes are held until the server has compared them.
    return mutate(addDocument, externalTitleSupportsUrl(host), 'POST', {
      external_title_support_id: uploaded.duplicateId,
      external_title_support: fields,
    });
  };

  // Post every item, then close (all linked) or stay open reporting "N of M linked".
  // onPartialFailure lets the caller reset inputs so a retry re-posts only what failed.
  const linkMany = async <T,>(
    items: T[],
    post: (item: T) => Promise<PostOutcome>,
    onPartialFailure: (failed: T[], succeeded: T[]) => void
  ): Promise<void> => {
    const results = await Promise.allSettled(items.map((item) => post(item)));
    const failed = items.filter((_, i) => !settledOk(results[i]));
    if (failed.length === 0) {
      onSaved();
      return;
    }

    const succeeded = items.filter((_, i) => settledOk(results[i]));
    onPartialFailure(failed, succeeded);
    // Refresh so the rows that did link show up behind the still-open panel.
    if (succeeded.length > 0) onRefresh?.();
    // One item is the common case, and its own reason says far more than a count —
    // "already linked to this tract" versus "0 of 1 linked; 1 failed".
    const reason = results
      .map((r) => (r.status === 'fulfilled' ? r.value.message : undefined))
      .find(Boolean);
    setError(
      items.length === 1 && reason
        ? reason
        : `${succeeded.length} of ${items.length} linked; ${failed.length} failed.`
    );
  };

  const handleAdd = async (): Promise<void> => {
    if (!kind) return;
    const ext = optionalExternal();

    if (activeConfig && RECORD_PICKER_KINDS.includes(kind)) {
      // Title and details are not sent: the backend snapshots them from the
      // linked record, the same way an edit re-syncs them.
      await linkMany(
        Array.from(selectedRecords.entries()),
        ([, row]) =>
          postOne({
            related_item: {
              kind,
              target_type: activeConfig.targetType,
              target_id: activeConfig.getRowId(row),
              source_downloadable: activeConfig.downloadable?.(row) ?? false,
              ...ext,
            },
          }),
        // Keep only the failures selected so a retry never re-posts a row that
        // already linked.
        (failed) => {
          const failedIds = new Set(failed.map(([id]) => id));
          setSelectedRecords(
            (prev) => new Map([...prev].filter(([id]) => failedIds.has(id)))
          );
        }
      );
      return;
    }

    if (linkingWorkbooks) {
      // Linking is idempotent server-side, so retrying a partial failure cannot duplicate a row.
      await linkMany(
        Array.from(selectedRecords.entries()),
        ([id]) =>
          mutate(addDocument, externalTitleSupportsUrl(host), 'POST', {
            external_title_support_id: id,
          }),
        (failed) => {
          const failedIds = new Set(failed.map(([id]) => id));
          setSelectedRecords(
            (prev) => new Map([...prev].filter(([id]) => failedIds.has(id)))
          );
        }
      );
      return;
    }

    if (isWorkbook) {
      await linkMany(completedFiles, uploadWorkbook, (_failed, succeeded) =>
        succeeded.forEach((f) =>
          dispatchFiles({ type: 'hide', localId: f.localId })
        )
      );
      return;
    }

    if (kind === 'uploaded_file') {
      const notes = fileNotes.trim();
      await linkMany(
        completedFiles,
        (f) =>
          postOne({
            related_item: {
              kind: 'uploaded_file',
              attachment: f.file,
              ...(notes ? { body: notes } : {}),
            },
          }),
        // Hide the uploads that succeeded so a retry only re-posts the failures.
        (_failed, succeeded) =>
          succeeded.forEach((f) =>
            dispatchFiles({ type: 'hide', localId: f.localId })
          )
      );
      return;
    }

    if (kind === 'external_link') {
      const url = extUrl.trim();
      const label = extLabel.trim();
      const res = await postOne({
        related_item: {
          kind: 'external_link',
          url,
          ...(label ? { label } : {}),
          title: label || url,
        },
      });
      if (res.ok) onSaved();
      else setError(res.message ?? 'Failed to link document.');
      return;
    }

    if (kind === 'note') {
      const body = note.trim();
      const res = await postOne({
        related_item: {
          kind: 'note',
          body,
          title: body.slice(0, NOTE_TITLE_MAX),
        },
      });
      if (res.ok) onSaved();
      else setError(res.message ?? 'Failed to link document.');
    }
  };

  const handleEdit = async (): Promise<boolean> => {
    if (!document) return false;
    let payload: Record<string, unknown>;

    if (kind === 'note') {
      const body = note.trim();
      payload = { body, title: body.slice(0, NOTE_TITLE_MAX) };
    } else if (kind === 'external_link') {
      const url = extUrl.trim();
      const label = extLabel.trim();
      payload = { url, label, title: label || url };
    } else if (isWorkbook) {
      // Not a related_item with a `details` snapshot.
      const res = await mutate(
        updateDocument,
        relatedItemWriteUrl(host, document),
        'PATCH',
        { external_title_support: curatedFields() }
      );
      if (!res.ok) setError(res.message ?? 'Failed to update document.');
      return res.ok;
    } else if (kind === 'uploaded_file') {
      // Send Notes as-is (empty string clears it); file_name only when set, since a blank
      // rename is a no-op and Save is disabled while the label is empty.
      payload = {
        file_name: fileLabel.trim() || undefined,
        body: fileNotes.trim(),
      };
    } else {
      // record-ish kinds: amend the optional external link. Send both as strings so
      // clearing the label persists rather than leaving the old one in place.
      payload = { url: extUrl.trim(), label: extLabel.trim() };
    }

    const res = await mutate(
      updateDocument,
      relatedItemWriteUrl(host, document),
      'PATCH',
      { related_item: payload }
    );
    if (!res.ok) setError(res.message ?? 'Failed to update document.');
    return res.ok;
  };

  const handleSubmit = async () => {
    // Provision mode: the section owns the POST + its own pending/onSaved handling.
    if (!isEdit && provisionMode) {
      provisionRef.current?.submit();
      return;
    }

    // Its endpoints are the tract's, not related_items'.
    if (linkingVesting) {
      vestingRef.current?.submit();
      return;
    }

    setPending(true);
    setError(null);
    try {
      if (isEdit) {
        const ok = await handleEdit();
        if (ok) onSaved();
      } else {
        await handleAdd();
      }
    } finally {
      setPending(false);
    }
  };

  const selectedIds = useMemo(
    () => new Set(selectedRecords.keys()),
    [selectedRecords]
  );

  // URL + optional label inputs, shared by the External Link kind and the
  // optional external-link sub-section on record / uploaded-file kinds.
  const urlLabelInputs = (urlId: string, labelId: string): ReactNode => (
    <>
      <TextInput
        id={urlId}
        labelText="URL"
        placeholder="https://"
        size="sm"
        light
        value={extUrl}
        invalid={externalUrlInvalid}
        invalidText="Enter a URL that starts with http:// or https://"
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setExtUrl(e.target.value)
        }
      />
      <TextInput
        id={labelId}
        labelText="Label (Optional)"
        placeholder="Link label (e.g. View on EnergyLink)"
        size="sm"
        light
        value={extLabel}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setExtLabel(e.target.value)
        }
      />
    </>
  );

  const renderKindFields = (): ReactNode => {
    if (!kind) return null;

    if (kind === 'note') {
      return (
        <div className={styles.field}>
          <TextArea
            id="link-document-note"
            labelText="Note"
            placeholder="Enter note text…"
            light
            value={note}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
              setNote(e.target.value)
            }
          />
        </div>
      );
    }

    if (kind === 'external_link') {
      return (
        <div className={styles.field}>
          {urlLabelInputs('link-document-url', 'link-document-url-label')}
        </div>
      );
    }

    if (linkingVesting) {
      return (
        <VestingDocumentSection
          ref={vestingRef}
          host={host}
          context={host.vesting}
          onReadyChange={setVestingReady}
          onPendingChange={setPending}
          onSaved={onSaved}
          multiple
        />
      );
    }

    if (kind === 'uploaded_file' || isWorkbook) {
      return (
        <div className={styles.field}>
          {/* Link before upload: the workbook usually already exists. */}
          {isWorkbook && !isEdit && (
            <div className={styles.field}>
              <ContentSwitcher
                size="sm"
                selectedIndex={uploadNewWorkbook ? 1 : 0}
                onChange={({ index }: { index?: number }) => {
                  setUploadNewWorkbook(index === 1);
                  setError(null);
                  setSelectedRecords(new Map());
                  dispatchFiles({ type: 'reset' });
                }}
              >
                <Switch name="link" text="Link existing" />
                <Switch name="upload" text="Upload new" />
              </ContentSwitcher>
            </div>
          )}
          {linkingWorkbooks ? (
            <RecordPickerTable
              key="external_title_support"
              config={workbookPicker}
              selectedIds={selectedIds}
              onToggle={toggleRecord}
            />
          ) : isEdit ? (
            // A workbook has no editable label; only `uploaded_file` gets this input.
            !isWorkbook && (
              <TextInput
                id="link-document-file-label"
                labelText="Label"
                placeholder="Document label"
                size="sm"
                light
                value={fileLabel}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setFileLabel(e.target.value)
                }
              />
            )
          ) : (
            <>
              <FileDropField
                files={visibleFiles}
                accept={isWorkbook ? WORKBOOK_ACCEPT : undefined}
                onAdd={(added) =>
                  dispatchFiles({ type: 'addNew', files: added })
                }
                onRemove={(localId) => dispatchFiles({ type: 'hide', localId })}
              />
            </>
          )}
          {isWorkbook && !linkingWorkbooks && (
            <ExternalTitleSupportFields
              value={supportDetails}
              onChange={setSupportDetails}
            />
          )}
          {/* Source, coverage and notes belong to the workbook, not this tract's link. */}
          {!linkingWorkbooks && (
            <TextArea
              id="link-document-file-notes"
              labelText="Notes (Optional)"
              placeholder="Add notes…"
              light
              value={fileNotes}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                setFileNotes(e.target.value)
              }
            />
          )}
        </div>
      );
    }

    // Record kinds: edit shows the linked-record summary; add shows the picker.
    if (isEdit) {
      return (
        <div className={styles.editSummary}>
          <span className={styles.editSummaryLabel}>Linked Record</span>
          <span className={styles.editSummaryValue}>
            {document?.title ?? '—'}
          </span>
        </div>
      );
    }

    if (activeConfig) {
      const provisionable = isProvisionableKind(kind);
      return (
        <>
          {provisionable && (
            <div className={styles.field}>
              <ContentSwitcher
                size="sm"
                selectedIndex={provisionMode ? 1 : 0}
                onChange={({ index }: { index?: number }) => {
                  const upload = index === 1;
                  setProvisionMode(upload);
                  setProvisionReady(false);
                  setError(null);
                  if (upload) setSelectedRecords(new Map());
                }}
              >
                <Switch name="search" text="Search existing" />
                <Switch name="upload" text="Upload & provision" />
              </ContentSwitcher>
            </div>
          )}
          {provisionable && provisionMode ? (
            <ProvisionUploadSection
              ref={provisionRef}
              host={host}
              kind={kind}
              onReadyChange={setProvisionReady}
              onPendingChange={setPending}
              onSaved={onSaved}
            />
          ) : (
            <RecordPickerTable
              key={kind}
              config={activeConfig}
              selectedIds={selectedIds}
              onToggle={toggleRecord}
            />
          )}
        </>
      );
    }

    return null;
  };

  const renderExternalSection = (): ReactNode => {
    if (!showExternalSection) return null;
    return (
      <>
        <div className={styles.externalDivider}>
          Add Optional Supporting External Link
        </div>
        <div className={styles.externalRow}>
          {urlLabelInputs('link-document-ext-url', 'link-document-ext-label')}
        </div>
      </>
    );
  };

  useEscapeToClose(open, onClose);

  return (
    <>
      <SidePanelResizeHandle
        open={open}
        panelSelector=".related-items-sidepanel"
        pageContentSelector={selectorPageContent}
        defaultWidthPx={640}
        showGrip
      />
      <SidePanel
        open={open}
        onRequestClose={onClose}
        title={isEdit ? 'Edit Related Item' : 'Link Item'}
        subtitle={isEdit ? 'Update item details.' : addSubtitle}
        className="related-items-sidepanel"
        slideIn
        selectorPageContent={selectorPageContent}
        placement="right"
        size="lg"
        actions={[
          { kind: 'secondary' as const, label: 'Cancel', onClick: onClose },
          {
            kind: 'primary' as const,
            label: pending ? 'Saving…' : 'Save',
            disabled: !canSubmit,
            onClick: handleSubmit,
          },
        ]}
      >
        <div className={styles.body}>
          {error && (
            <InlineNotification
              kind="error"
              lowContrast
              title="Error"
              subtitle={error}
              onCloseButtonClick={() => setError(null)}
            />
          )}

          <div className={styles.typeSelect}>
            <Dropdown
              id="link-document-type"
              titleText="Item Type"
              label="Select item type"
              items={kindOptions}
              itemToString={(item: RelatedItemTypeOption | null) => {
                return item ? item.label : '';
              }}
              selectedItem={kindOptions.find((o) => o.kind === kind) ?? null}
              disabled={isEdit}
              size="sm"
              onChange={({
                selectedItem,
              }: {
                selectedItem: RelatedItemTypeOption | null;
              }) => handleKindChange(selectedItem?.kind ?? null)}
            />
          </div>

          <div className={styles.fields}>
            {renderKindFields()}
            {renderExternalSection()}
          </div>
        </div>
      </SidePanel>
    </>
  );
};
